import { useEffect, useState } from "react";
import { getData } from "../api";
import { Box, Typography, Select, MenuItem, FormControl, InputLabel, Checkbox, FormGroup, FormControlLabel, Button } from "@mui/material";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import theme from "../styles/theme";
import {
  SPECIAL_CATEGORIES,
  filterOutSpecialCategories,
  getExercisesForSpecialCategory,
  loadSpecialAssignments,
} from "../utils/specialCategories";

interface User {
  id: number;
  username: string;
  nom_complet?: string;
}

interface Session {
  id: number;
  name: string;
  start_date: string;
}

interface Category {
  id: number;
  name: string;
}

interface Evaluation {
  id: number;
  player_id: number;
  exercise_id: number;
  session_id: number;
  details: string;
}

interface Exercise {
  id: number;
  max_points: number;
  repetitions: number;
  category_id: number;
}

type MetricOption = {
  id: string;
  name: string;
  kind: "category" | "special";
};

type SpecialAssignments = Record<string, Record<string, boolean>>;

export default function PlayerEvolution() {
  const [players, setPlayers] = useState<User[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<string>("");
  const [selectedSessions, setSelectedSessions] = useState<number[]>([]);
  const [specialAssignments, setSpecialAssignments] = useState<SpecialAssignments>({});
  // Pour afficher/masquer les courbes des joueurs
  const [visiblePlayers, setVisiblePlayers] = useState<{ [username: string]: boolean }>({});

  useEffect(() => {
    const initial: { [username: string]: boolean } = {};
    players.forEach((player) => {
      initial[player.username] = true;
    });
    setVisiblePlayers(initial);
  }, [players]);

  useEffect(() => {
    setSpecialAssignments(loadSpecialAssignments());

    getData("users", { where: "role = 'player'" }).then(async (res: any) => {
      const users = res.data || [];
      const playersWithProfiles = await Promise.all(
        users.map(async (user: any) => {
          const profileRes = await getData("profiles", { where: `user_id = '${user.id}'` });
          const profile = profileRes.data?.[0] || {};
          return { ...user, nom_complet: profile.nom_complet };
        })
      );
      setPlayers(playersWithProfiles);
    });
    getData("sessions", { order_by: "start_date ASC" }).then((res: any) => {
      setSessions(res.data || []);
      if (res.data && res.data.length > 0) {
        setSelectedSessions(res.data.map((s: any) => s.id)); // Par défaut, toutes cochées
      }
    });
    getData("exercise_categories").then((res: any) =>
      setCategories(filterOutSpecialCategories(res.data || []))
    );
    getData("exercises").then((res: any) => setExercises(res.data || []));
    getData("evaluations").then((res: any) => setEvaluations(res.data || []));
  }, []);

  // Map exercise_id -> category_id
  const exerciseToCategory: { [id: number]: number } = {};
  exercises.forEach((ex) => {
    exerciseToCategory[ex.id] = ex.category_id;
  });

  // Map category_id -> name
  const categoryMap: { [id: number]: string } = {};
  categories.forEach((c) => { categoryMap[c.id] = c.name; });

  const specialMetricOptions: MetricOption[] = SPECIAL_CATEGORIES.map((special) => ({
    id: special.id,
    name: `${special.icon} ${special.name}`,
    kind: "special",
  }));

  const metricOptions: MetricOption[] = [
    ...categories.map((cat) => ({
      id: String(cat.id),
      name: cat.name,
      kind: "category" as const,
    })),
    ...specialMetricOptions,
  ];

  const visibleMetricOptions = metricOptions.filter((metric) => {
    if (metric.kind === "category") return true;
    return getExercisesForSpecialCategory(specialAssignments, metric.id).length > 0;
  });

  const metricLabelMap = visibleMetricOptions.reduce<Record<string, string>>((acc, metric) => {
    acc[metric.id] = metric.name;
    return acc;
  }, {});

  const calculateAverageRate = (evals: Evaluation[]) => {
    if (evals.length === 0) return 0;

    let total = 0;
    let count = 0;

    evals.forEach((ev) => {
      const ex = exercises.find((e) => e.id === ev.exercise_id);
      if (!ex) return;

      const scores: number[] = JSON.parse(ev.details || "[]");
      const max = (ex.max_points || 0) * (ex.repetitions || 0);
      const sum = scores.reduce((a, b) => a + b, 0);

      if (max > 0) {
        total += (sum / max) * 100;
        count++;
      }
    });

    return count > 0 ? parseFloat((total / count).toFixed(2)) : 0;
  };

  const getSessionEvaluationsForMetric = (
    playerId: number,
    sessionId: number,
    metric: MetricOption | null
  ) => {
    return evaluations.filter((ev) => {
      if (ev.player_id !== playerId || ev.session_id !== sessionId) return false;

      if (!metric) return true;

      if (metric.kind === "category") {
        return exerciseToCategory[ev.exercise_id] === Number(metric.id);
      }

      const specialExerciseIds = getExercisesForSpecialCategory(specialAssignments, metric.id);
      return specialExerciseIds.includes(ev.exercise_id);
    });
  };

  // Détection du cas : un seul joueur visible
  const visiblePlayerUsernames = Object.keys(visiblePlayers).filter((u) => visiblePlayers[u]);
  const isSinglePlayer = visiblePlayerUsernames.length === 1;
  // Si un seul joueur, on prépare les données par catégorie
  let singlePlayerData: { [metricId: string]: { [sessionId: number]: number } } = {};
  let singlePlayerId: number | null = null;
  if (isSinglePlayer) {
    const player = players.find((p) => p.username === visiblePlayerUsernames[0]);
    if (player) {
      singlePlayerId = player.id;
      visibleMetricOptions.forEach((metric) => {
        singlePlayerData[metric.id] = {};
        sessions.forEach((session) => {
          const evals = getSessionEvaluationsForMetric(
            player.id,
            session.id,
            metric
          );
          singlePlayerData[metric.id][session.id] = calculateAverageRate(evals);
        });
      });
    }
  }

  // Cas normal : multi-joueurs, une catégorie
  const data: { [playerId: number]: { [sessionId: number]: number } } = {};
  const selectedMetricOption =
    visibleMetricOptions.find((metric) => metric.id === selectedMetric) || null;

  players.forEach((player) => {
    data[player.id] = {};
    sessions.forEach((session) => {
      const evals = getSessionEvaluationsForMetric(
        player.id,
        session.id,
        selectedMetricOption
      );
      data[player.id][session.id] = calculateAverageRate(evals);
    });
  });

  return (
    <Box sx={{ padding: 2, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h2" sx={{ mb: 3, color: theme.palette.primary.main }}>
        Évolution par catégorie
      </Typography>
      {/* Liste des joueurs pour afficher/masquer leur courbe */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr 1fr', sm: 'repeat(auto-fit, minmax(180px, 1fr))' },
          gap: "5px",
          mb: 2,
          justifyContent: 'center',
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            // Réactive tous les joueurs
            const allVisible: { [username: string]: boolean } = {};
            players.forEach((p) => { allVisible[p.username] = true; });
            console.log('Voir tous les joueurs, allVisible:', allVisible);
            setVisiblePlayers(allVisible);
          }}
          sx={{
            color: theme.palette.primary.main,
            border: `1px solid ${theme.palette.primary.main}`,
            fontWeight: 700,
            marginRight: 2,
            minWidth: 120,
            marginBottom: 1,
          }}
        >
          Voir tous
        </Button>
        {players.map((player) => {
          const isHidden = !visiblePlayers[player.username];
          return (
            <Button
              key={player.id}
              variant="text"
              onClick={() => {
                // Lorsqu'on clique sur un nom, on affiche uniquement ce joueur (grise tous les autres)
                setVisiblePlayers((prev) => {
                  const newState: { [username: string]: boolean } = {};
                  players.forEach((p) => {
                    newState[p.username] = p.username === player.username;
                  });
                  console.log('Click joueur', player.username, '-> newState:', newState);
                  return newState;
                });
              }}
              sx={{
                color: isHidden ? '#aaa' : theme.palette.primary.main,
                textDecoration: isHidden ? 'line-through' : 'none',
                fontWeight: isHidden ? 400 : 700,
                fontStyle: isHidden ? 'italic' : 'normal',
                border: isHidden ? `1px dashed #aaa` : `1px solid ${theme.palette.primary.main}`,
                background: isHidden ? '#f5f5f5' : 'transparent',
                opacity: isHidden ? 0.6 : 1,
                minWidth: 120,
                marginBottom: 1,
              }}
            >
              {player.nom_complet || player.username}
            </Button>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel id="cat-select-label">Catégorie</InputLabel>
          <Select
            labelId="cat-select-label"
            value={selectedMetric}
            label="Catégorie"
            onChange={(e) => setSelectedMetric(String(e.target.value))}
          >
            <MenuItem value="">Toutes</MenuItem>
            {visibleMetricOptions.map((metric) => (
              <MenuItem key={metric.id} value={metric.id}>{metric.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl component="fieldset" sx={{ minWidth: 300 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>Sessions à comparer :</Typography>
          <FormGroup row>
            {sessions.map((session) => (
              <FormControlLabel
                key={session.id}
                control={
                  <Checkbox
                    checked={selectedSessions.includes(session.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedSessions([...selectedSessions, session.id]);
                      } else {
                        setSelectedSessions(selectedSessions.filter((id) => id !== session.id));
                      }
                    }}
                  />
                }
                label={session.name}
              />
            ))}
          </FormGroup>
        </FormControl>
      </Box>
      {/* Graphique d'évolution multi-joueurs OU mono-joueur multi-catégories */}
      <Box sx={{ mt: 4 }}>
        <ResponsiveContainer width="100%" height={500}>
          <LineChart
            data={selectedSessions.map((sid) => {
              const entry: any = { session: sessions.find((s) => s.id === sid)?.name || sid };
              if (isSinglePlayer && singlePlayerId) {
                visibleMetricOptions.forEach((metric) => {
                  entry[metricLabelMap[metric.id]] = singlePlayerData[metric.id]?.[sid] ?? 0;
                });
              } else {
                players.forEach((player) => {
                  entry[player.nom_complet || player.username] = data[player.id][sid] ?? 0;
                });
              }
              return entry;
            })}
            margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="session" />
            <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
            <Tooltip
              formatter={(v: any) => `${v} %`}
              content={(props: any) => {
                if (!props.active || !props.payload || !props.payload.length) return null;
                const sorted = [...props.payload].sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
                return (
                  <div style={{ background: '#fff', border: '1px solid #ccc', padding: 10, borderRadius: 6 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{props.label}</div>
                    {sorted.map((entry: any, idx: number) => (
                      <div key={entry.dataKey} style={{ color: entry.color, fontWeight: 500 }}>
                        {entry.dataKey}: {entry.value} %
                      </div>
                    ))}
                  </div>
                );
              }}
            />
            <Legend
              onClick={(e: any) => {
                if (!e || !e.dataKey) return;
                if (isSinglePlayer) return; // Pas de masquage par légende en mode mono-joueur
                setVisiblePlayers((prev) => ({ ...prev, [e.dataKey]: !prev[e.dataKey] }));
              }}
              wrapperStyle={{ cursor: isSinglePlayer ? 'default' : 'pointer' }}
            />
            {isSinglePlayer && singlePlayerId
              ? visibleMetricOptions.map((metric, idx) => (
                  <Line
                    key={metric.id}
                    type="monotone"
                    dataKey={metricLabelMap[metric.id]}
                    stroke={['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'][idx % 8]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 6 }}
                  />
                ))
              : players.map((player, idx) => (
                  visiblePlayers[player.username] && (
                    <Line
                      key={player.id}
                      type="monotone"
                      dataKey={player.nom_complet || player.username}
                      stroke={['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'][idx % 8]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 6 }}
                    />
                  )
                ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
